import { apiV2, FirebaseError } from "api/firebase";
import { FullProduct, ShowProductConfig } from "ts/api/product";
import ProductColour from "ts/api/productColours";
import {
  CommonNotificationType,
  CustomProductOptionConfigType,
  IShow,
  IProductCategoryWithName,
  INotificationBaseMessage,
  INotificationBaseRelatedToShow,
  IProduct,
  IProductCategory,
  ISupplierCompanyProductTag,
  ISupplierCompanyToProductCategory,
  NotificationType,
  SurchargeType,
} from "@evennt-tech/api-v2-core";
import {
  IAssignProductsToShowAsyncRes,
  IAssignProductsToShowBodyReq,
  IGetAllProductsByShowAndIdsQueryReq,
  IGetManyProductsByShowQueryReq,
  IGetOneOrganiserCompanyRes,
  IGetProductFiltersByShowQueryReq,
  IProductShowAttachment,
  IProductShowCategoryWithProducts,
  IProductFilters,
  IRemoveProductFromShowQueryReq,
  ISetHideBuildTypesOnShowBodyReq,
  ISetProductsVisibilityOnShowBodyReq,
} from "@evennt-tech/api-v2-external";
import {
  matchesProductChange,
  PRODUCT_CHANGE_KEYS,
  subscribeToProductChanges as subscribeToBroadcastProductChanges,
} from "store/product/product-notifications";
import {
  getCategoryPagesCount,
  lastLoadedPage,
  MAX_PRODUCTS_PER_CATEGORY_REQUEST,
  runStartPage,
  withLoadedPage,
  withProductPaging,
} from "store/product/grouped-categories";
import {
  calculatePaymentFeeAmount,
  calculatePlatformFeeAmount,
  calculateProductPrice,
  calculateProductPriceWithFees,
  FetchedProductColour,
} from "hooks/helpers";
import { create } from "zustand";
import { devtools } from "zustand/middleware";
import { registerStore } from "store/utils/reset-store.utils";
import filterNotEmptyArray from "tools/common/filterNotEmptyArray";
import expandCustomConfig from "tools/product/expandCustomConfig";
import expandShowProduct, {
  expandRecommendedProducts,
  ShowProductContext,
} from "tools/product/expandShowProduct";
import SupplierCompany from "ts/api/supplierCompany";
import { customProductOptionShopStore } from "store/custom-product-option/custom-product-option-shop.store";
import { parseISO } from "date-fns";
import { useProductCategoryStore } from "store/product-category/product-category.store";
import { useOrganiserCompany } from "store/organiser-company/organiser-company.store";

type Pagination = {
  page: number;
  limit: number;
  total: number;
};

export const SHOW_PRODUCTS_PER_CATEGORY = 10;

export enum ProductExpansionScope {
  SHOW = "show",
  BY_IDS = "byIds",
}

interface ShowProductsState {
  products: FullProduct[];
  categories: IProductCategoryWithName[];
  colors: ProductColour[];
  tags: ISupplierCompanyProductTag[];
  isLoading: boolean;
  isReady: boolean;
  error: FirebaseError | null;
  organiserCompany: IGetOneOrganiserCompanyRes | null;
  show: IShow | null;
  // Table view — paged (each page REPLACES the previous one).
  groupedProducts: IProductShowCategoryWithProducts[];
  groupedProductsPagination: Pagination;
  groupedProductsParams: GroupedProductsByShowParams | null;
  groupedProductsLoadedParams: GroupedProductsByShowParams | null;
  groupedProductsLoading: boolean;
  groupedCategoriesLoadingMore: boolean;
  groupedCategoriesLoadingMoreBefore: boolean;
  loadedCategoryPages: number[];
  activeCategoryPageFrom: number;
  categoryIdsByPage: Record<number, string[]>;
  priorityCategoryLoadingIds: string[];
  groupedCategoryLoading: Record<string, boolean>;
  groupedCategoryPages: Record<string, number>;
  groupedCategoryLimits: Record<string, number>;
  // Shop (card) view — "Show more" APPENDS the next page to the loaded ones.
  shopGroupedProducts: IProductShowCategoryWithProducts[];
  shopGroupedCategoryLoading: Record<string, boolean>;
  shopGroupedCategoryPages: Record<string, number>;
  // Flat infinite-scroll list (e.g. QuickOrder's product picker) — paginated,
  // each new page APPENDS to the accumulated list.
  manyByShowProducts: IProduct[];
  manyByShowPagination: Pagination;
  manyByShowParams: IGetManyProductsByShowQueryReq | null;
  // Exact products fetched by id (e.g. the Cart) — no pagination, so items are
  // never dropped because of a page boundary.
  productsByIds: IProduct[];
  productsByIdsParams: IGetAllProductsByShowAndIdsQueryReq | null;
  productsByIdsLoadedParams: IGetAllProductsByShowAndIdsQueryReq | null;
  productsByIdsLoading: boolean;
  expandedProductIds: Set<string>;
  expandedScope: ProductExpansionScope | null;
  productFiltersByShow: IProductFilters | null;
}

interface ShowProductsActions {
  setProducts: (data: {
    products: FullProduct[];
    categories: IProductCategoryWithName[];
    colors: ProductColour[];
  }) => void;
  setIsLoading: (isLoading: boolean) => void;
  setIsReady: (isReady: boolean) => void;
  setError: (error: FirebaseError | null) => void;
  setOrganiserCompany: (
    organiserCompany: IGetOneOrganiserCompanyRes | null
  ) => void;
  setShow: (show: IShow | null) => void;
  resetShowScopedData: () => void;
  fetchProducts: (params: {
    showProducts: IProduct[] | undefined;
    showSupplierCompanies: SupplierCompany[] | undefined;
    show: IShow | undefined;
    organiserCompanyToShow: IGetOneOrganiserCompanyRes | null | undefined;
    isLoading: boolean;
    showId: string;
    scope: ProductExpansionScope;
    categoryCodesBySupplier: Map<string, ISupplierCompanyToProductCategory[]>;
    fetchCategoryCodes: (
      supplierCompanyIds: string[],
      categoryIds: string[]
    ) => Promise<void>;
  }) => Promise<FullProduct[] | undefined>;
  updateConfigsByProductId: (productId: string) => void;
  assignToShowAsync: (
    body: IAssignProductsToShowBodyReq
  ) => Promise<IAssignProductsToShowAsyncRes>;
  removeFromShow: (params: IRemoveProductFromShowQueryReq) => Promise<void>;
  setProductsVisibilityOnShow: (
    body: ISetProductsVisibilityOnShowBodyReq
  ) => Promise<void>;
  setHideBuildTypesOnShow: (
    body: ISetHideBuildTypesOnShowBodyReq
  ) => Promise<void>;
  fetchManyByShowGroupedByCategory: (
    params: GroupedProductsByShowParams,
    options?: { preserveLoadedPages?: boolean }
  ) => Promise<void>;
  loadMoreGroupedCategories: () => Promise<void>;
  loadMoreGroupedCategoriesBefore: () => Promise<void>;
  loadPriorityCategory: (categoryId: string) => Promise<void>;
  setGroupedCategoryPage: (
    categoryId: string,
    page: number,
    options?: { limit?: number }
  ) => Promise<void>;
  loadMoreShopCategory: (categoryId: string) => Promise<void>;
  showLessShopCategory: (categoryId: string) => void;
  autoCatchManyByShowGroupedByCategory: () => void;
  fetchManyByShow: (params: IGetManyProductsByShowQueryReq) => Promise<void>;
  fetchProductDetails: (
    productId: string
  ) => Promise<{
    recommendedProducts: FullProduct[];
    attachments: IProductShowAttachment[];
  }>;
  autoCatchManyByShow: () => void;
  refetchRawProducts: (showId?: string) => Promise<void>;
  getAllByShowAndProductIds: (
    params: IGetAllProductsByShowAndIdsQueryReq
  ) => Promise<void>;
  autoCatchProductsByIds: () => void;
  fetchProductFiltersByShow: (
    params: IGetProductFiltersByShowQueryReq
  ) => Promise<void>;
}

const realCategoryIds = (items: IProductShowCategoryWithProducts[]): string[] =>
  items
    .filter(
      ({ products, totalProducts }) => !!products.length || !totalProducts
    )
    .map(({ id }) => id);

const mergeGroupedCategories = <T extends { id: string; products: unknown[] }>(
  loaded: T[],
  incoming: T[]
): T[] => {
  const byId = new Map<string, T>();

  loaded.forEach((group) => byId.set(group.id, group));

  incoming.forEach((group) => {
    const current = byId.get(group.id);

    if (!current || group.products.length > current.products.length) {
      byId.set(group.id, group);
    }
  });

  return Array.from(byId.values());
};

export type GroupedProductsByShowParams = Omit<
  IGetManyProductsByShowQueryReq,
  "page" | "limit"
>;

const SHOW_NOTIFICATION_KEYS = [
  ...PRODUCT_CHANGE_KEYS,
  NotificationType.UPDATE_CUSTOM_PRODUCT_OPTION_CONFIGURATION_OVERRIDES,
  NotificationType.ASSIGN_SUPPLIER_COMPANY_TO_SHOW,
  NotificationType.EDIT_SUPPLIER_COMPANY_TO_SHOW,
  NotificationType.DELETE_SUPPLIER_COMPANY_FROM_SHOW,
];

// Published without a show scope, but they all change what a product costs or
// whether it can be sold: its configs, the CPO definitions behind its options,
// the organiser-to-supplier relation, plus the supplier's Stripe connect
// account.
const COMMON_NOTIFICATION_KEYS = [
  CommonNotificationType.UPDATE_CUSTOM_PRODUCT_OPTION_CONFIGURATIONS,
  CommonNotificationType.UPDATE_SUPPLIER_CUSTOM_PRODUCT_OPTION,
  CommonNotificationType.UPDATE_PRODUCT_COLOR,
  NotificationType.ASSIGN_SUPPLIER_COMPANY,
  NotificationType.CREATED_STRIPE_CONNECT_ACCOUNT,
  NotificationType.DELETED_STRIPE_CONNECT_ACCOUNT,
];

// Fees live on the organiser company, and prices are calculated from it while
// expanding, so the company itself has to be refetched before the products.
const ORGANISER_COMPANY_FEE_KEYS = [
  NotificationType.UPDATED_ORGANISER_COMPANY_FEE,
  NotificationType.UPDATED_ORGANISER_COMPANY_PAYMENT_FEE,
];

// Published with a show scope when the change belongs to one show and without
// it otherwise, so both variants have to be caught.
const SHOW_AND_COMMON_NOTIFICATION_KEYS = [
  CommonNotificationType.UPDATE_SURCHARGE,
];

// Every notification key drives its own refetch, so responses can come back out
// of order. Only the latest request of each raw fetch is allowed to write.
let groupedRequestId = 0;
let manyByShowRequestId = 0;
let productsByIdsRequestId = 0;
let expandRequestId = 0;
// Last context built while expanding the listing — reused to expand the
// recommended products the drawer pulls on demand.
let lastProductContext: ShowProductContext | null = null;

const showScopedListeners = new Map<string, () => void>();

const subscribeShowScopedKeys = (showId: string) => {
  showScopedListeners.forEach((callback, listenerIdPrefix) => {
    for (const key of SHOW_AND_COMMON_NOTIFICATION_KEYS) {
      apiV2.notification.subscribe<INotificationBaseRelatedToShow>({
        key,
        showId,
        listenerId: `${listenerIdPrefix}_${key}_show`,
        callback,
      });
    }
  });
};

const subscribeToProductChanges = ({
  getShowId,
  listenerIdPrefix,
  callback,
}: {
  getShowId: () => string | undefined;
  listenerIdPrefix: string;
  callback: () => void;
}) => {
  subscribeToBroadcastProductChanges({
    keys: SHOW_NOTIFICATION_KEYS,
    listenerIdPrefix,
    callback: (payload) => {
      if (!matchesProductChange(payload, { showId: getShowId() })) return;

      callback();
    },
  });

  subscribeToBroadcastProductChanges({
    keys: COMMON_NOTIFICATION_KEYS,
    listenerIdPrefix,
    callback: () => callback(),
  });

  for (const key of SHOW_AND_COMMON_NOTIFICATION_KEYS) {
    apiV2.notification.subscribe<INotificationBaseMessage>({
      key,
      listenerId: `${listenerIdPrefix}_${key}_common`,
      callback,
    });
  }

  for (const key of ORGANISER_COMPANY_FEE_KEYS) {
    apiV2.notification.subscribe<INotificationBaseMessage>({
      key,
      listenerId: `${listenerIdPrefix}_${key}_fee`,
      callback: async () => {
        const showId = getShowId();

        if (showId) {
          await useOrganiserCompany.getState().fetchOneByShowId({ showId });
        }

        callback();
      },
    });
  }

  showScopedListeners.set(listenerIdPrefix, callback);

  const showId = getShowId();
  if (showId) subscribeShowScopedKeys(showId);
};

export const useShowProductsStore = create<
  ShowProductsState & ShowProductsActions
>()(
  devtools((set, get) => ({
    products: [],
    categories: [],
    colors: [],
    isLoading: false,
    isReady: false,
    error: null,
    tags: [],
    organiserCompany: null,
    show: null,
    groupedProducts: [],
    groupedProductsPagination: {
      page: 1,
      limit: SHOW_PRODUCTS_PER_CATEGORY,
      total: 0,
    },
    groupedProductsParams: null,
    groupedProductsLoadedParams: null,
    groupedProductsLoading: false,
    groupedCategoriesLoadingMore: false,
    groupedCategoriesLoadingMoreBefore: false,
    loadedCategoryPages: [],
    activeCategoryPageFrom: 1,
    categoryIdsByPage: {},
    priorityCategoryLoadingIds: [],
    groupedCategoryLoading: {},
    groupedCategoryPages: {},
    groupedCategoryLimits: {},
    shopGroupedProducts: [],
    shopGroupedCategoryLoading: {},
    shopGroupedCategoryPages: {},
    manyByShowProducts: [],
    manyByShowPagination: {
      page: 1,
      limit: SHOW_PRODUCTS_PER_CATEGORY,
      total: 0,
    },
    manyByShowParams: null,
    productsByIds: [],
    productsByIdsParams: null,
    productsByIdsLoadedParams: null,
    productsByIdsLoading: false,
    expandedProductIds: new Set(),
    expandedScope: null,
    productFiltersByShow: null,

    setProducts: (data) => set({ ...data }),
    setIsLoading: (isLoading) => set({ isLoading }),
    setIsReady: (isReady) => set({ isReady }),
    setError: (error) => set({ error }),
    setOrganiserCompany: (organiserCompany) => set({ organiserCompany }),
    setShow: (show) => set({ show }),

    resetShowScopedData: () =>
      set({
        products: [],
        categories: [],
        colors: [],
        tags: [],
        show: null,
        organiserCompany: null,
        isReady: false,
        expandedProductIds: new Set(),
        expandedScope: null,
        productFiltersByShow: null,
        groupedProducts: [],
        groupedProductsParams: null,
        groupedProductsLoadedParams: null,
        loadedCategoryPages: [],
        activeCategoryPageFrom: 1,
        categoryIdsByPage: {},
        groupedCategoryLoading: {},
        groupedCategoryPages: {},
        groupedCategoryLimits: {},
        groupedProductsPagination: {
          page: 1,
          limit: SHOW_PRODUCTS_PER_CATEGORY,
          total: 0,
        },
        shopGroupedProducts: [],
        shopGroupedCategoryLoading: {},
        shopGroupedCategoryPages: {},
        manyByShowProducts: [],
        manyByShowParams: null,
        manyByShowPagination: {
          page: 1,
          limit: SHOW_PRODUCTS_PER_CATEGORY,
          total: 0,
        },
        productsByIds: [],
        productsByIdsParams: null,
        productsByIdsLoadedParams: null,
        productsByIdsLoading: false,
      }),

    fetchProducts: async ({
      showProducts,
      showSupplierCompanies,
      show,
      organiserCompanyToShow,
      isLoading,
      showId,
      scope,
      categoryCodesBySupplier,
      fetchCategoryCodes,
    }) => {
      await useProductCategoryStore.getState().fetchAll({ mainOnly: true });

      const mainProductCategories = useProductCategoryStore.getState().items;

      if (
        !showProducts ||
        !showSupplierCompanies ||
        !show ||
        isLoading ||
        !organiserCompanyToShow ||
        !mainProductCategories?.length
      ) {
        return;
      }

      let colors: FetchedProductColour = {};
      const tagIdsSet = new Set<string>();
      const recommendedProducts = showProducts.flatMap((product) =>
        (product?.recommendedProducts ?? [])
          .map(({ recommendedProduct }) => recommendedProduct)
          .filter((recommended): recommended is IProduct => !!recommended)
      );
      const productsToExpand = [...showProducts, ...recommendedProducts];
      const supplierCompanyIds = Array.from(
        new Set(
          productsToExpand
            .map((p) => p?.supplierCompanyId)
            .filter((id): id is string => id != null)
        )
      );
      const productConfigsBySupplier = customProductOptionShopStore
        .getState()
        .setFromProducts(productsToExpand, showId);

      const {
        categoryIds,
        supplierCompanyIds: missingCodeSupplierIds,
      } = productsToExpand.reduce<{
        supplierCompanyIds: Set<string>;
        categoryIds: Set<string>;
      }>(
        (acc, curr) => {
          if (
            !curr?.supplierCompanyId ||
            categoryCodesBySupplier.has(curr.supplierCompanyId)
          ) {
            return acc;
          }
          acc.supplierCompanyIds.add(curr.supplierCompanyId);
          const categoryId = curr.categories?.[0]?.mainCategoryId;
          if (categoryId) {
            acc.categoryIds.add(categoryId);
          }
          return acc;
        },
        { supplierCompanyIds: new Set(), categoryIds: new Set() }
      );

      if (categoryIds.size) {
        await fetchCategoryCodes(
          Array.from(missingCodeSupplierIds),
          Array.from(categoryIds)
        );
        return;
      }

      expandRequestId += 1;
      const expansionId = expandRequestId;

      const [
        supplierCompanies,
        supplierCompanyShowManagers,
      ] = await Promise.all([
        apiV2.supplierCompany.getAllByIds({
          supplierCompanyIds: supplierCompanyIds?.join(","),
          withProductCategories: true,
        }),
        apiV2.supplierCompany.getShowManagersBySupplierCompanyIds({
          showId,
          organiserCompanyId: organiserCompanyToShow.id,
          supplierCompanyIds: supplierCompanyIds?.join(","),
          withSupplierCompanyShowProductCategories: true,
        }),
      ]);

      const supplierCompanyMap = new Map(
        supplierCompanies.map((company) => [company.id, company])
      );
      const showManagerMap = new Map(
        supplierCompanyShowManagers
          .filter(Boolean)
          .map((manager) => [
            manager?.organiserCompanyToSupplierCompany?.supplierCompanyId,
            manager,
          ])
      );

      lastProductContext = {
        show,
        showId,
        organiserCompany: organiserCompanyToShow,
        supplierCompanies: supplierCompanyMap,
        showManagers: showManagerMap,
        categoryCodesBySupplier,
        productConfigsBySupplier,
        colors,
        tagIds: tagIdsSet,
      };
      const productContext: ShowProductContext = lastProductContext;

      const products = showProducts
        .map((showProduct) => {
          if (!showProduct) {
            return null;
          }

          const productConfigsAndFiles = productConfigsBySupplier
            .get(showProduct.supplierCompanyId)
            ?.get(showProduct.id);

          return expandShowProduct(
            {
              product: showProduct,
              configs: productConfigsAndFiles?.configs ?? [],
              files: productConfigsAndFiles?.files,
              recommendedProducts: expandRecommendedProducts(
                showProduct,
                productContext
              ),
            },
            productContext
          );
        })
        .filter(filterNotEmptyArray);

      let tags: ISupplierCompanyProductTag[] = [];
      if (tagIdsSet.size) {
        try {
          tags = await apiV2.supplierCompany.findProductTagsByIds({
            ids: Array.from(tagIdsSet),
          });
        } catch (e) {
          console.error("Error during loading product tags: ", e);
        }
      }

      const categoriesWithProducts: Record<string, IProductCategory> = {};
      products.forEach((product) => {
        product.categories.forEach(
          ({ mainCategory, mainCategoryId, subCategory }) => {
            if (!mainCategory) {
              return;
            }

            if (!categoriesWithProducts[mainCategoryId]) {
              categoriesWithProducts[mainCategoryId] = {
                ...mainCategory,
                id: mainCategoryId,
                subCategories: subCategory ? [subCategory] : [],
              };
            } else if (subCategory) {
              const existing = categoriesWithProducts[mainCategoryId];
              const combinedSubs = [
                ...(existing.subCategories || []),
                subCategory,
              ];
              existing.subCategories = Array.from(
                new Map(combinedSubs.map((c) => [c.id, c])).values()
              );
            }
          }
        );
      });

      if (expansionId === expandRequestId) {
        set({
          products,
          colors: Object.values(colors),
          categories: Object.values(categoriesWithProducts),
          organiserCompany: organiserCompanyToShow,
          tags,
          show,
          expandedProductIds: new Set(showProducts.map(({ id }) => id)),
          expandedScope: scope,
        });
      }

      return products;
    },

    updateConfigsByProductId: (productId) => {
      const { products, organiserCompany, show } = get();
      const product = products.find((p) => p.id === productId);
      if (!product || !organiserCompany || !show) return;

      const defaultConfig = product.configs.find(
        (c) => c.type === CustomProductOptionConfigType.DEFAULT
      );
      if (!defaultConfig) return;

      const supplierFee = product.ownerToShow?.organiserFee ?? 0;
      const organiserFee = organiserCompany.fee ?? 0;
      const { paymentFee } = organiserCompany;
      const surcharges = product.surcharges ?? [];
      const buildUp = parseISO(show.deliveryStart);

      const calculatedPrice = calculateProductPrice(
        buildUp,
        surcharges,
        defaultConfig.priceUplift,
        defaultConfig.showPrice
      );
      const price =
        calculatedPrice === SurchargeType.unpublish
          ? null
          : (calculatedPrice as number);

      const priceWithFees =
        price !== null
          ? calculateProductPriceWithFees(price, organiserFee, supplierFee)
          : 0;

      const platformFeeAmount =
        organiserFee && price
          ? calculatePlatformFeeAmount(price, organiserFee)
          : null;

      const paymentFeeAmount =
        paymentFee && price
          ? calculatePaymentFeeAmount(price, paymentFee)
          : null;

      const updatedConfigs = product.configs.map(
        (config): ShowProductConfig => {
          if (config.type === CustomProductOptionConfigType.DEFAULT) {
            return {
              ...config,
              price,
              priceWithFees,
              ...(platformFeeAmount !== null && { platformFeeAmount }),
              paymentFeeAmount,
              purchasedQuantity: config.purchasedQuantity ?? 0,
            };
          }

          return expandCustomConfig(config, {
            defaultPrice: defaultConfig.priceUplift,
            defaultShowPrice: defaultConfig.showPrice,
            supplierFee,
            organiserFee,
            paymentFee,
            surcharges,
            buildUp,
          });
        }
      );

      set((state) => {
        const { products } = state;
        const fullUpdatedProducts: FullProduct[] = products.map((p) =>
          p.id === productId
            ? ({
                ...p,
                configs: updatedConfigs,
                price,
                priceWithFees,
                paymentFeeAmount,
                platformFeeAmount,
                organiserFee,
                basePrice: defaultConfig.priceUplift,
                showPrice: defaultConfig.showPrice,
              } as FullProduct)
            : p
        );
        return {
          products: fullUpdatedProducts,
        };
      });
    },

    assignToShowAsync: async (body) => {
      return apiV2.product.assignToShowAsync.submitAsync(body);
    },

    removeFromShow: async (params) => {
      await apiV2.product.removeFromShow(params);
    },

    setProductsVisibilityOnShow: async (body) => {
      await apiV2.product.setProductsVisibilityOnShow(body);
    },

    setHideBuildTypesOnShow: async (body) => {
      await apiV2.product.setHideBuildTypesOnShow(body);
    },

    fetchManyByShowGroupedByCategory: async (params, options) => {
      subscribeShowScopedKeys(params.showId);

      const pageSize = SHOW_PRODUCTS_PER_CATEGORY;
      const preserveLoadedPages = !!options?.preserveLoadedPages;
      const loadedPages = preserveLoadedPages
        ? [
            ...Object.values(get().shopGroupedCategoryPages),
            ...Object.values(get().groupedCategoryPages),
          ]
        : [];
      // A refresh has to bring back everything already on screen ("Show more"
      // pages included), otherwise the list silently collapses to page 1.
      const maxLoadedPage = loadedPages.length
        ? Math.max(1, ...loadedPages)
        : 1;
      // The table view can page a category with a larger rows-per-page, so a
      // refresh has to bring back its window as well.
      const loadedTableWindows = preserveLoadedPages
        ? Object.entries(get().groupedCategoryLimits).map(
            ([categoryId, categoryLimit]) =>
              (get().groupedCategoryPages[categoryId] ?? 1) * categoryLimit
          )
        : [];
      const restoredPages = preserveLoadedPages
        ? get().loadedCategoryPages
        : [];
      const categoryPages = restoredPages.length ? restoredPages : [1];
      groupedRequestId += 1;
      const requestId = groupedRequestId;

      set({
        isLoading: true,
        groupedProductsLoading: true,
        groupedProductsParams: params,
      });

      try {
        const productsLimit = Math.min(
          Math.max(pageSize * maxLoadedPage, ...loadedTableWindows, 0),
          MAX_PRODUCTS_PER_CATEGORY_REQUEST
        );

        let items: IProductShowCategoryWithProducts[] = [];
        let totalItems = 0;
        let itemsPerPage = 0;
        const categoryIdsByPage: Record<number, string[]> = {};

        for (const categoryPage of categoryPages) {
          const categoryPageData = await apiV2.product.show.getManyGroupedByCategory(
            withProductPaging(
              {
                ...params,
                page: categoryPage,
              },
              {
                productsPage: 1,
                productsLimit,
              }
            )
          );

          if (requestId !== groupedRequestId) {
            return;
          }

          items = mergeGroupedCategories(items, categoryPageData.items);
          totalItems = categoryPageData.meta.totalItems ?? totalItems;
          itemsPerPage = categoryPageData.meta.itemsPerPage ?? itemsPerPage;
          categoryIdsByPage[categoryPage] = realCategoryIds(
            categoryPageData.items
          );
        }

        const data = { items, meta: { totalItems, itemsPerPage } };

        if (!preserveLoadedPages) {
          // Seed both views from the same initial page-1 payload; their
          // pagination diverges afterwards (table replaces, shop appends).
          set({
            groupedProducts: data.items,
            shopGroupedProducts: data.items,
            groupedProductsPagination: {
              page: 1,
              limit: data.meta.itemsPerPage,
              total: data.meta.totalItems ?? 0,
            },
            groupedProductsLoadedParams: params,
            groupedCategoryPages: {},
            groupedCategoryLimits: {},
            shopGroupedCategoryPages: {},
            loadedCategoryPages: [1],
            activeCategoryPageFrom: 1,
            categoryIdsByPage,
            expandedProductIds: new Set<string>(),
            expandedScope: null,
          });
          return;
        }

        set((state) => {
          const groupedCategoryPages: Record<string, number> = {};
          const shopGroupedCategoryPages: Record<string, number> = {};

          const groupedProducts = data.items.map((category) => {
            const categoryLimit =
              state.groupedCategoryLimits[category.id] ?? pageSize;
            const maxPage = Math.max(
              1,
              Math.ceil(category.totalProducts / categoryLimit)
            );
            const page = Math.min(
              state.groupedCategoryPages[category.id] ?? 1,
              maxPage
            );
            groupedCategoryPages[category.id] = page;

            const products = category.products.slice(
              (page - 1) * categoryLimit,
              page * categoryLimit
            );

            return {
              ...category,
              products,
              hasMore:
                !!products.length &&
                category.totalProducts > page * categoryLimit,
            };
          });

          const shopGroupedProducts = data.items.map((category) => {
            const maxPage = Math.max(
              1,
              Math.ceil(category.totalProducts / pageSize)
            );
            const page = Math.min(
              state.shopGroupedCategoryPages[category.id] ?? 1,
              maxPage
            );
            shopGroupedCategoryPages[category.id] = page;

            const products = category.products.slice(0, page * pageSize);

            return {
              ...category,
              products,
              hasMore:
                !!products.length && products.length < category.totalProducts,
            };
          });

          return {
            groupedProducts,
            shopGroupedProducts,
            groupedProductsPagination: {
              page: lastLoadedPage(categoryPages),
              limit: data.meta.itemsPerPage,
              total: data.meta.totalItems ?? 0,
            },
            groupedProductsLoadedParams: params,
            groupedCategoryPages,
            shopGroupedCategoryPages,
            loadedCategoryPages: categoryPages,
            categoryIdsByPage,
            expandedProductIds: new Set<string>(),
          };
        });
      } catch (e) {
        console.error(e);
      } finally {
        if (requestId === groupedRequestId) {
          set({ isLoading: false, groupedProductsLoading: false });
        }
      }
    },

    loadMoreGroupedCategories: async () => {
      const {
        groupedProductsParams,
        groupedProductsPagination,
        groupedProductsLoading,
        groupedCategoriesLoadingMore,
        loadedCategoryPages,
      } = get();

      const nextPage = lastLoadedPage(loadedCategoryPages) + 1;

      if (
        !groupedProductsParams ||
        groupedProductsLoading ||
        groupedCategoriesLoadingMore ||
        nextPage >
          getCategoryPagesCount(
            groupedProductsPagination.total,
            groupedProductsPagination.limit
          )
      ) {
        return;
      }

      set({ groupedCategoriesLoadingMore: true });

      try {
        const data = await apiV2.product.show.getManyGroupedByCategory(
          withProductPaging(
            {
              ...groupedProductsParams,
              page: nextPage,
            },
            {
              productsPage: 1,
              productsLimit: SHOW_PRODUCTS_PER_CATEGORY,
            }
          )
        );

        set((state) => ({
          groupedProducts: mergeGroupedCategories(
            state.groupedProducts,
            data.items
          ),
          shopGroupedProducts: mergeGroupedCategories(
            state.shopGroupedProducts,
            data.items
          ),
          groupedProductsPagination: {
            page: nextPage,
            limit: data.meta.itemsPerPage,
            total:
              data.meta.totalItems ?? state.groupedProductsPagination.total,
          },
          loadedCategoryPages: withLoadedPage(
            state.loadedCategoryPages,
            nextPage
          ),
          categoryIdsByPage: {
            ...state.categoryIdsByPage,
            [nextPage]: realCategoryIds(data.items),
          },
        }));
      } catch (e) {
        console.error(e);
      } finally {
        set({ groupedCategoriesLoadingMore: false });
      }
    },

    loadMoreGroupedCategoriesBefore: async () => {
      const {
        groupedProductsParams,
        groupedProductsLoading,
        groupedCategoriesLoadingMoreBefore,
        loadedCategoryPages,
        activeCategoryPageFrom,
      } = get();

      const previousPage = activeCategoryPageFrom - 1;

      if (
        !groupedProductsParams ||
        groupedProductsLoading ||
        groupedCategoriesLoadingMoreBefore ||
        previousPage < 1
      ) {
        return;
      }

      if (loadedCategoryPages.includes(previousPage)) {
        set({
          activeCategoryPageFrom: runStartPage(
            loadedCategoryPages,
            previousPage
          ),
        });

        return;
      }

      set({ groupedCategoriesLoadingMoreBefore: true });

      try {
        const data = await apiV2.product.show.getManyGroupedByCategory(
          withProductPaging(
            {
              ...groupedProductsParams,
              page: previousPage,
            },
            {
              productsPage: 1,
              productsLimit: SHOW_PRODUCTS_PER_CATEGORY,
            }
          )
        );

        set((state) => {
          const pages = withLoadedPage(state.loadedCategoryPages, previousPage);

          return {
            groupedProducts: mergeGroupedCategories(
              state.groupedProducts,
              data.items
            ),
            shopGroupedProducts: mergeGroupedCategories(
              state.shopGroupedProducts,
              data.items
            ),
            groupedProductsPagination: {
              ...state.groupedProductsPagination,
              total:
                data.meta.totalItems ?? state.groupedProductsPagination.total,
            },
            loadedCategoryPages: pages,
            activeCategoryPageFrom: runStartPage(pages, previousPage),
            categoryIdsByPage: {
              ...state.categoryIdsByPage,
              [previousPage]: realCategoryIds(data.items),
            },
          };
        });
      } catch (e) {
        console.error(e);
      } finally {
        set({ groupedCategoriesLoadingMoreBefore: false });
      }
    },

    loadPriorityCategory: async (categoryId) => {
      const {
        groupedProductsParams,
        groupedProducts,
        shopGroupedProducts,
        priorityCategoryLoadingIds,
      } = get();

      const hasLoadedProducts = (
        groups: IProductShowCategoryWithProducts[]
      ): boolean =>
        groups.some(
          ({ id, mainCategoryId, products }) =>
            (id === categoryId || mainCategoryId === categoryId) &&
            !!products.length
        );

      if (
        !groupedProductsParams ||
        priorityCategoryLoadingIds.includes(categoryId) ||
        (hasLoadedProducts(groupedProducts) &&
          hasLoadedProducts(shopGroupedProducts))
      ) {
        return;
      }

      set((state) => ({
        priorityCategoryLoadingIds: [
          ...state.priorityCategoryLoadingIds,
          categoryId,
        ],
      }));

      try {
        const data = await apiV2.product.show.getManyGroupedByCategory(
          withProductPaging(
            {
              ...groupedProductsParams,
              page: 1,
            },
            {
              priorityCategoryId: categoryId,
              productsPage: 1,
              productsLimit: SHOW_PRODUCTS_PER_CATEGORY,
            }
          )
        );

        const page = Math.max(1, data.meta.currentPage);

        set((state) => {
          const pages = withLoadedPage(state.loadedCategoryPages, page);

          return {
            groupedProducts: mergeGroupedCategories(
              state.groupedProducts,
              data.items
            ),
            shopGroupedProducts: mergeGroupedCategories(
              state.shopGroupedProducts,
              data.items
            ),
            groupedProductsPagination: {
              page: lastLoadedPage(pages),
              limit: data.meta.itemsPerPage,
              total:
                data.meta.totalItems ?? state.groupedProductsPagination.total,
            },
            loadedCategoryPages: pages,
            activeCategoryPageFrom: runStartPage(pages, page),
            categoryIdsByPage: {
              ...state.categoryIdsByPage,
              [page]: realCategoryIds(data.items),
            },
          };
        });
      } catch (e) {
        console.error(e);
      } finally {
        set((state) => ({
          priorityCategoryLoadingIds: state.priorityCategoryLoadingIds.filter(
            (id) => id !== categoryId
          ),
        }));
      }
    },

    setGroupedCategoryPage: async (categoryId, page, options) => {
      const {
        groupedProductsParams,
        groupedCategoryLoading,
        groupedCategoryLimits,
      } = get();

      if (!groupedProductsParams || groupedCategoryLoading[categoryId]) return;

      const limit =
        options?.limit ??
        groupedCategoryLimits[categoryId] ??
        SHOW_PRODUCTS_PER_CATEGORY;

      set((state) => ({
        groupedCategoryLoading: {
          ...state.groupedCategoryLoading,
          [categoryId]: true,
        },
      }));

      try {
        const data = await apiV2.product.show.getManyGroupedByCategory(
          withProductPaging(
            { ...groupedProductsParams, page: 1 },
            {
              categoryId,
              productsPage: page,
              productsLimit: limit,
            }
          )
        );

        const group = data.items.find(({ id }) => id === categoryId);

        if (!group) return;

        set((state) => ({
          groupedProducts: state.groupedProducts.map((category) =>
            category.id === categoryId
              ? {
                  ...category,
                  products: group.products,
                  totalProducts: group.totalProducts,
                  hasMore: group.totalProducts > page * limit,
                }
              : category
          ),
          groupedCategoryPages: {
            ...state.groupedCategoryPages,
            [categoryId]: page,
          },
          groupedCategoryLimits: {
            ...state.groupedCategoryLimits,
            [categoryId]: limit,
          },
        }));
      } catch (e) {
        console.error(e);
      } finally {
        set((state) => ({
          groupedCategoryLoading: {
            ...state.groupedCategoryLoading,
            [categoryId]: false,
          },
        }));
      }
    },

    loadMoreShopCategory: async (categoryId) => {
      const {
        groupedProductsParams,
        shopGroupedCategoryLoading,
        shopGroupedCategoryPages,
      } = get();

      if (!groupedProductsParams || shopGroupedCategoryLoading[categoryId])
        return;

      const nextPage = (shopGroupedCategoryPages[categoryId] ?? 1) + 1;

      set((state) => ({
        shopGroupedCategoryLoading: {
          ...state.shopGroupedCategoryLoading,
          [categoryId]: true,
        },
      }));

      try {
        // The grouped endpoint — the one page 1 came from — and NOT the flat
        // `getManyByShow`. Each orders a category differently (grouped applies
        // the supplier-defined order and interleaves suppliers; flat is a plain
        // `createdAt DESC` OFFSET), so paging through the flat one returned
        // rows page 1 had already rendered. The dedup below then dropped every
        // one of them and the list froze short of its total.
        const data = await apiV2.product.show.getManyGroupedByCategory(
          withProductPaging(
            {
              ...groupedProductsParams,
              page: 1,
            },
            {
              categoryId,
              productsPage: nextPage,
              productsLimit: SHOW_PRODUCTS_PER_CATEGORY,
            }
          )
        );

        // `meta` of the grouped response counts CATEGORIES, not products — the
        // per-category totals live on the group itself.
        const group = data.items.find(({ id }) => id === categoryId);

        if (!group) return;

        set((state) => ({
          shopGroupedProducts: state.shopGroupedProducts.map((category) => {
            if (category.id !== categoryId) return category;

            const fetchedById = new Map(
              group.products.map((item) => [item.id, item])
            );
            const existingIds = new Set(category.products.map((p) => p.id));
            const mergedProducts = [
              ...category.products.map(
                (product) => fetchedById.get(product.id) ?? product
              ),
              ...group.products.filter((item) => !existingIds.has(item.id)),
            ];

            return {
              ...category,
              products: mergedProducts,
              totalProducts: group.totalProducts,
              // Measured against what is actually on screen rather than
              // `nextPage * limit`: the dedup above can drop items, and a
              // page-count estimate would then hide the button while products
              // are still missing.
              hasMore: mergedProducts.length < group.totalProducts,
            };
          }),
          shopGroupedCategoryPages: {
            ...state.shopGroupedCategoryPages,
            [categoryId]: nextPage,
          },
          shopGroupedCategoryLoading: {
            ...state.shopGroupedCategoryLoading,
            [categoryId]: false,
          },
        }));
      } catch (e) {
        console.error(e);
      } finally {
        set((state) =>
          state.shopGroupedCategoryLoading[categoryId]
            ? {
                shopGroupedCategoryLoading: {
                  ...state.shopGroupedCategoryLoading,
                  [categoryId]: false,
                },
              }
            : {}
        );
      }
    },

    showLessShopCategory: (categoryId) => {
      set((state) => {
        const currentPage = state.shopGroupedCategoryPages[categoryId] ?? 1;
        if (currentPage <= 1) return {};

        const prevPage = currentPage - 1;

        return {
          shopGroupedProducts: state.shopGroupedProducts.map((category) => {
            if (category.id !== categoryId) return category;

            const products = category.products.slice(
              0,
              prevPage * SHOW_PRODUCTS_PER_CATEGORY
            );

            return {
              ...category,
              products,
              // Same rule as loadMore: compare what is left on screen against
              // the total, not the page count — collapsing a list whose pages
              // were deduped would otherwise hide the button with products
              // still unseen.
              hasMore: products.length < category.totalProducts,
            };
          }),
          shopGroupedCategoryPages: {
            ...state.shopGroupedCategoryPages,
            [categoryId]: prevPage,
          },
        };
      });
    },

    autoCatchManyByShowGroupedByCategory: () => {
      subscribeToProductChanges({
        getShowId: () => get().groupedProductsParams?.showId,
        listenerIdPrefix: "product_show_grouped_store",
        callback: () => {
          const params = get().groupedProductsParams;

          if (!params) return;

          get().fetchManyByShowGroupedByCategory(params, {
            preserveLoadedPages: true,
          });
        },
      });
    },

    // Flat, paginated fetch for infinite-scroll consumers. Page > 1 appends
    // (dedup by id) so scrolling accumulates products.
    fetchProductDetails: async (productId) => {
      const empty = { recommendedProducts: [], attachments: [] };
      const { groupedProductsParams, manyByShowParams, show } = get();
      const showId =
        groupedProductsParams?.showId ?? manyByShowParams?.showId ?? show?.id;

      if (!showId || !lastProductContext) return empty;

      const exhibitorPairId =
        groupedProductsParams?.exhibitorPairId ??
        manyByShowParams?.exhibitorPairId;

      try {
        const product = await apiV2.product.show.getOne({
          id: productId,
          showId,
          ...(exhibitorPairId && { exhibitorPairId }),
        });

        return {
          recommendedProducts: expandRecommendedProducts(product as IProduct, {
            ...lastProductContext,
            productConfigsBySupplier: customProductOptionShopStore
              .getState()
              .cacheRecommendedProducts(product as IProduct, showId),
          }),
          attachments: product.attachments ?? [],
        };
      } catch (e) {
        console.error("Error during loading product details: ", e);
        return empty;
      }
    },

    fetchManyByShow: async (params) => {
      subscribeShowScopedKeys(params.showId);

      manyByShowRequestId += 1;
      const requestId = manyByShowRequestId;

      set({ isLoading: true, manyByShowParams: params });

      try {
        const data = await apiV2.product.show.getMany(params);

        if (requestId !== manyByShowRequestId) {
          return;
        }

        set((state) => {
          const isLoadMore = (params.page ?? 1) > 1;
          const items = isLoadMore
            ? (() => {
                // Already loaded rows are replaced by the freshly fetched ones
                // (prices and stock change), the rest is appended.
                const fetchedById = new Map(
                  data.items.map((item) => [item.id, item])
                );
                const existingIds = new Set(
                  state.manyByShowProducts.map((product) => product.id)
                );
                return [
                  ...state.manyByShowProducts.map(
                    (product) => fetchedById.get(product.id) ?? product
                  ),
                  ...data.items.filter((item) => !existingIds.has(item.id)),
                ];
              })()
            : data.items;

          return {
            manyByShowProducts: items,
            manyByShowPagination: {
              page: data.meta.currentPage,
              limit: data.meta.itemsPerPage,
              total: data.meta.totalItems ?? 0,
            },
            ...(!isLoadMore && {
              expandedProductIds: new Set<string>(),
            }),
          };
        });
      } catch (e) {
        console.error(e);
      } finally {
        if (requestId === manyByShowRequestId) {
          set({ isLoading: false });
        }
      }
    },

    autoCatchManyByShow: () => {
      subscribeToProductChanges({
        getShowId: () => get().manyByShowParams?.showId,
        listenerIdPrefix: "product_show_store",
        // Params are read from the store, not from this closure: the gateway
        // keeps the callback registered first for a given listener id, so a
        // captured page would stay frozen at whatever it was on mount.
        // One request for the whole accumulated window, so rows from earlier
        // pages are refreshed too instead of keeping their old prices.
        callback: () => {
          const currentParams = get().manyByShowParams;

          if (!currentParams) return;

          const pageSize = currentParams.limit ?? SHOW_PRODUCTS_PER_CATEGORY;

          get().fetchManyByShow({
            ...currentParams,
            page: 1,
            limit: (currentParams.page ?? 1) * pageSize,
          });
        },
      });
    },

    refetchRawProducts: async (showId) => {
      const {
        groupedProductsParams,
        manyByShowParams,
        productsByIdsParams,
      } = get();
      const isCurrentShow = (params: { showId: string }) =>
        !showId || params.showId === showId;

      const requests: Promise<void>[] = [];

      if (groupedProductsParams && isCurrentShow(groupedProductsParams)) {
        requests.push(
          get().fetchManyByShowGroupedByCategory(groupedProductsParams, {
            preserveLoadedPages: true,
          })
        );
      }
      if (manyByShowParams && isCurrentShow(manyByShowParams)) {
        requests.push(get().fetchManyByShow(manyByShowParams));
      }
      if (productsByIdsParams && isCurrentShow(productsByIdsParams)) {
        requests.push(get().getAllByShowAndProductIds(productsByIdsParams));
      }

      await Promise.all(requests);
    },

    getAllByShowAndProductIds: async (params) => {
      subscribeShowScopedKeys(params.showId);

      productsByIdsRequestId += 1;
      const requestId = productsByIdsRequestId;

      set({
        isLoading: true,
        productsByIdsLoading: true,
        productsByIdsParams: params,
      });

      try {
        const items = await apiV2.product.show.getAllByIds(params);

        if (requestId !== productsByIdsRequestId) {
          return;
        }

        set({
          productsByIds: items,
          productsByIdsLoadedParams: params,
          expandedProductIds: new Set<string>(),
        });
      } catch (e) {
        console.error(e);
      } finally {
        if (requestId === productsByIdsRequestId) {
          set({ isLoading: false, productsByIdsLoading: false });
        }
      }
    },

    autoCatchProductsByIds: () => {
      subscribeToProductChanges({
        getShowId: () => get().productsByIdsParams?.showId,
        listenerIdPrefix: "product_show_by_ids_store",
        callback: () => {
          const params = get().productsByIdsParams;

          if (!params) return;

          get().getAllByShowAndProductIds(params);
        },
      });
    },

    fetchProductFiltersByShow: async (params) => {
      try {
        const productFiltersByShow = await apiV2.product.getProductFiltersByShow(
          params
        );
        set({ productFiltersByShow });
      } catch (e) {
        console.error("Error during loading product filters: ", e);
      }
    },
  }))
);

registerStore(useShowProductsStore);
